/**
 * App - Root application component that wires together all major features.
 *
 * Flow:
 * 1. Initialize auth (restore session from stored tokens)
 * 2. AuthGuard wraps the main app (redirects to login if unauthenticated)
 * 3. MainLayout provides responsive sidebar + content layout
 * 4. CalendarView lives in the sidebar, EntryEditor in the content area
 * 5. SyncStatusIndicator shows sync state in the header
 * 6. WebSocket client initializes on auth success
 * 7. Offline detection wires to sync store
 * 8. Auto-save wires to entry store updates
 *
 * @see Requirements 4.6, 5.4, 6.2, 8.1, 8.3
 */

import { useEffect, useCallback, useState, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useEntryStore } from '@/stores/entryStore';
import { useSyncStore } from '@/stores/syncStore';
import { useUIStore } from '@/stores/uiStore';
import { AuthGuard } from '@/components/auth/AuthGuard';
import { LoginScreen } from '@/components/auth/LoginScreen';
import { RegisterScreen } from '@/components/auth/RegisterScreen';
import { VerificationScreen } from '@/components/auth/VerificationScreen';
import { AppShell } from '@/components/layout/AppShell';
import { MainLayout } from '@/components/layout/MainLayout';
import { CalendarView } from '@/components/calendar/CalendarView';
import { EntryEditor } from '@/components/editor/EntryEditor';
import { SyncStatusIndicator } from '@/components/sync/SyncStatusIndicator';
import { ConflictNotification } from '@/components/sync/ConflictResolution';
import { WebSocketClient } from '@/services/websocket';
import {
  SyncQueue,
  type SyncApiClient,
} from '@/services/syncQueue';
import { useAutoSave } from '@/hooks/useAutoSave';
import { usePomodoroTick } from '@/hooks/usePomodoroTick';
import { format } from 'date-fns';
import { getIdToken } from '@/services/auth';
import type { JournalEntry, SyncResponse, SyncQueueItem } from '@/types';
import { SyncStatus, SyncAction } from '@/types/enums';

// ─── Constants ───────────────────────────────────────────────────────────────

const WS_URL = import.meta.env.VITE_WEBSOCKET_URL ?? 'wss://ws.api.journalapp.com';
const API_BASE_URL = import.meta.env.VITE_API_URL ?? import.meta.env.VITE_API_BASE_URL ?? '/api';

// ─── Auth Screens ────────────────────────────────────────────────────────────

type AuthScreen = 'login' | 'register' | 'verify';

/**
 * AuthScreens handles the unauthenticated state with login/register/verify flows.
 */
function AuthScreens() {
  const [screen, setScreen] = useState<AuthScreen>('login');
  const [verifyEmail, setVerifyEmail] = useState('');
  const { login, register } = useAuthStore();

  const handleLogin = useCallback(async (email: string, password: string) => {
    await login(email, password);
  }, [login]);

  const handleRegister = useCallback(async (email: string, password: string) => {
    await register(email, password);
    setVerifyEmail(email);
    setScreen('verify');
  }, [register]);

  const handleVerify = useCallback(async (_code: string) => {
    // Verification is handled via Amplify's confirmSignUp
    const { TokenManager } = await import('@/services/auth');
    await TokenManager.confirmEmail(verifyEmail, _code);
    setScreen('login');
  }, [verifyEmail]);

  const handleSocialLogin = useCallback(async (_provider: 'google' | 'apple') => {
    // Social login triggers a redirect via Amplify; no immediate state change
    const { TokenManager } = await import('@/services/auth');
    await TokenManager.socialLogin(_provider);
  }, []);

  switch (screen) {
    case 'register':
      return (
        <RegisterScreen
          onRegister={handleRegister}
          onNavigateLogin={() => setScreen('login')}
        />
      );
    case 'verify':
      return (
        <VerificationScreen
          email={verifyEmail}
          onVerify={handleVerify}
          onNavigateLogin={() => setScreen('login')}
        />
      );
    case 'login':
    default:
      return (
        <LoginScreen
          onLogin={handleLogin}
          onSocialLogin={handleSocialLogin}
          onNavigateRegister={() => setScreen('register')}
        />
      );
  }
}

// ─── API Client for Sync Queue ───────────────────────────────────────────────

function createSyncApiClient(): SyncApiClient {
  return {
    async fetchSyncEntries(since: string): Promise<SyncResponse> {
      const token = await getIdToken();
      const response = await fetch(
        `${API_BASE_URL}/entries/sync?since=${encodeURIComponent(since)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        throw new Error(`Sync fetch failed: ${response.status}`);
      }
      return response.json();
    },

    async pushEntry(item: SyncQueueItem): Promise<JournalEntry> {
      const token = await getIdToken();
      const method = item.action === 'delete' ? 'DELETE' : 'PUT';
      const response = await fetch(
        `${API_BASE_URL}/entries/${item.entryDate}`,
        {
          method,
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: method === 'DELETE' ? undefined : JSON.stringify({
            content: item.payload.content,
            version: item.payload.version,
            mediaRefs: item.payload.mediaRefs,
          }),
        }
      );
      if (!response.ok) {
        throw new Error(`Push entry failed: ${response.status}`);
      }
      return response.json();
    },
  };
}

// ─── Main App Content (Journal Module) ───────────────────────────────────────

/**
 * AppContent renders the Journal module content.
 * Will be lazy-loaded by ModuleRouter once task 10.4 is complete.
 * Handles CalendarView, EntryEditor, sync, and offline logic.
 */
export function AppContent() {
  // ─── Pomodoro timer tick — runs regardless of active module (Req 7.8, 7.9) ──
  usePomodoroTick();

  const {
    currentEntry,
    calendarDates,
    loadCalendarDates,
    createEntry,
    updateEntry,
    setCurrentEntry,
  } = useEntryStore();

  const { isAuthenticated, userId } = useAuthStore();
  const { setOnlineStatus, setSyncStatus, setLastSyncTime, setQueueSize, addConflict, conflicts } = useSyncStore();
  const { selectedDate, setSelectedDate } = useUIStore();

  const [apiTimeout, setApiTimeout] = useState(false);
  const [entryLoadError, setEntryLoadError] = useState(false);

  // Refs for WebSocket client and SyncQueue (persist across renders)
  const wsClientRef = useRef<WebSocketClient | null>(null);
  const syncQueueRef = useRef<SyncQueue | null>(null);

  // ─── Initialize WebSocket on auth success ──────────────────────────────────

  useEffect(() => {
    if (!isAuthenticated) return;

    const wsClient = new WebSocketClient({
      url: WS_URL,
      handlers: {
        onEntryChanged: (data) => {
          // Fetch the updated entry from the server and update local state
          void fetchEntryFromServer(data.entryDate);
        },
        onConflictDetected: (data) => {
          addConflict({
            conflictId: data.conflictId,
            userId: userId ?? '',
            entryDate: data.entryDate,
            versionA: { content: '', version: 0, updatedAt: '', mediaRefs: [], contentHash: '' },
            versionB: { content: '', version: 0, updatedAt: '', mediaRefs: [], contentHash: '' },
            createdAt: new Date().toISOString(),
            resolvedAt: null,
          });
        },
        onSyncComplete: (data) => {
          setLastSyncTime(data.serverTime);
          setSyncStatus('synced');
        },
        onConnected: () => {
          setSyncStatus('synced');
        },
        onDisconnected: () => {
          if (!navigator.onLine) {
            setSyncStatus('offline');
          }
        },
      },
    });

    wsClientRef.current = wsClient;
    void wsClient.connect();

    return () => {
      wsClient.disconnect();
      wsClientRef.current = null;
    };
  }, [isAuthenticated, userId, addConflict, setLastSyncTime, setSyncStatus]);

  // ─── Initialize Sync Queue ─────────────────────────────────────────────────

  useEffect(() => {
    if (!isAuthenticated) return;

    const syncQueue = new SyncQueue({
      apiClient: createSyncApiClient(),
      onConflict: (localEntry, serverEntry) => {
        addConflict({
          conflictId: `conflict_${Date.now()}`,
          userId: userId ?? '',
          entryDate: localEntry.entryDate,
          versionA: {
            content: localEntry.content,
            version: localEntry.version,
            updatedAt: localEntry.updatedAt,
            mediaRefs: localEntry.mediaRefs,
            contentHash: localEntry.contentHash,
          },
          versionB: {
            content: serverEntry.content,
            version: serverEntry.version,
            updatedAt: serverEntry.updatedAt,
            mediaRefs: serverEntry.mediaRefs,
            contentHash: serverEntry.contentHash,
          },
          createdAt: new Date().toISOString(),
          resolvedAt: null,
        });
      },
      onSyncComplete: (serverTime) => {
        setLastSyncTime(serverTime);
        setSyncStatus('synced');
      },
      onQueueSizeChange: (size) => {
        setQueueSize(size);
      },
    });

    syncQueueRef.current = syncQueue;

    return () => {
      syncQueue.stop();
      syncQueueRef.current = null;
    };
  }, [isAuthenticated, userId, addConflict, setLastSyncTime, setSyncStatus, setQueueSize]);

  // ─── Offline Detection ─────────────────────────────────────────────────────

  useEffect(() => {
    const handleOnline = () => {
      setOnlineStatus(true);
      // Begin sync within 10 seconds of detecting connectivity (Requirement 5.4)
      syncQueueRef.current?.handleReconnect();
      void wsClientRef.current?.connect();
    };

    const handleOffline = () => {
      setOnlineStatus(false);
    };

    // Set initial status
    setOnlineStatus(navigator.onLine);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [setOnlineStatus]);

  // ─── Load calendar dates on mount and month change ─────────────────────────

  useEffect(() => {
    const now = new Date();
    void loadCalendarDates(now.getFullYear(), now.getMonth() + 1);
  }, [loadCalendarDates]);

  // ─── Date selection handler ────────────────────────────────────────────────

  const handleDateSelect = useCallback(async (dateString: string) => {
    setSelectedDate(dateString);
    setEntryLoadError(false);
    setApiTimeout(false);

    try {
      // Try to load the entry from local DB or API
      const token = await getIdToken();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10_000);

      try {
        const response = await fetch(
          `${API_BASE_URL}/entries/${dateString}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            signal: controller.signal,
          }
        );
        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          setCurrentEntry({
            entryDate: data.entry.entryDate,
            content: data.entry.content,
            version: data.entry.version,
            localVersion: 0,
            updatedAt: data.entry.updatedAt,
            createdAt: data.entry.createdAt,
            mediaRefs: data.entry.mediaRefs ?? [],
            syncStatus: SyncStatus.Synced,
            contentHash: data.entry.contentHash ?? '',
            pendingMediaUploads: [],
          });
        } else if (response.status === 404) {
          // No entry exists yet, create new empty one
          await createEntry(dateString);
        } else {
          setEntryLoadError(true);
        }
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError instanceof DOMException && fetchError.name === 'AbortError') {
          // 10-second timeout — show service unavailable message
          setApiTimeout(true);
          // Allow local edits — create a local entry
          await createEntry(dateString);
        } else {
          setEntryLoadError(true);
        }
      }
    } catch {
      // Fallback: create a local entry
      await createEntry(dateString);
    }
  }, [setSelectedDate, setCurrentEntry, createEntry]);

  // ─── Entry content change handler ──────────────────────────────────────────

  const handleEditorChange = useCallback((json: string) => {
    if (!currentEntry) return;
    // Update entry in store optimistically
    void updateEntry(currentEntry.entryDate, json);
  }, [currentEntry, updateEntry]);

  // ─── Auto-save hook wired to entry store updates ───────────────────────────

  const handleSave = useCallback(async (entryDate: string, content: string) => {
    const token = await getIdToken();
    if (!token) return;

    setSyncStatus('syncing');

    const response = await fetch(`${API_BASE_URL}/entries/${entryDate}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        content,
        version: currentEntry?.version ?? 0,
        mediaRefs: currentEntry?.mediaRefs ?? [],
      }),
    });

    if (!response.ok) {
      throw new Error(`Save failed: ${response.status}`);
    }

    const data = await response.json();

    // Update local version with server-confirmed version
    if (currentEntry) {
      setCurrentEntry({
        ...currentEntry,
        version: data.entry.version,
        updatedAt: data.entry.updatedAt,
        syncStatus: SyncStatus.Synced,
        contentHash: data.entry.contentHash ?? '',
      });
    }

    // Notify WebSocket of the update
    wsClientRef.current?.sendEntryUpdated(
      entryDate,
      data.entry.version,
      data.entry.contentHash ?? ''
    );

    setSyncStatus('synced');
    setLastSyncTime(new Date().toISOString());

    // Also enqueue to sync queue for offline resilience
    if (syncQueueRef.current) {
      void syncQueueRef.current.enqueue(entryDate, SyncAction.Update, {
        content,
        version: data.entry.version,
        mediaRefs: currentEntry?.mediaRefs ?? [],
        contentHash: data.entry.contentHash ?? '',
      });
    }
  }, [currentEntry, setCurrentEntry, setSyncStatus, setLastSyncTime]);

  const { isSaving, saveError, retryCount } = useAutoSave({
    content: currentEntry?.content ?? '',
    entryDate: currentEntry?.entryDate ?? '',
    onSave: handleSave,
    onError: () => {
      // On save failure, enqueue for offline sync
      if (currentEntry && syncQueueRef.current) {
        void syncQueueRef.current.enqueue(
          currentEntry.entryDate,
          SyncAction.Update,
          {
            content: currentEntry.content,
            version: currentEntry.version,
            mediaRefs: currentEntry.mediaRefs,
            contentHash: currentEntry.contentHash,
          }
        );
      }
    },
  });

  // ─── Retry entry load ──────────────────────────────────────────────────────

  const handleRetryEntryLoad = useCallback(() => {
    if (selectedDate) {
      setEntryLoadError(false);
      void handleDateSelect(selectedDate);
    }
  }, [selectedDate, handleDateSelect]);

  // ─── Fetch a single entry from the server (triggered by WebSocket push) ────

  async function fetchEntryFromServer(entryDate: string) {
    try {
      const token = await getIdToken();
      const response = await fetch(`${API_BASE_URL}/entries/${entryDate}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        // If this is the currently open entry, update it
        if (currentEntry?.entryDate === entryDate) {
          setCurrentEntry({
            entryDate: data.entry.entryDate,
            content: data.entry.content,
            version: data.entry.version,
            localVersion: 0,
            updatedAt: data.entry.updatedAt,
            createdAt: data.entry.createdAt,
            mediaRefs: data.entry.mediaRefs ?? [],
            syncStatus: SyncStatus.Synced,
            contentHash: data.entry.contentHash ?? '',
            pendingMediaUploads: [],
          });
        }
      }
    } catch {
      // Silent failure for background sync — doesn't block the user
    }
  }

  // ─── Render ────────────────────────────────────────────────────────────────

  const sidebarContent = (
    <div className="flex flex-col gap-4">
      <CalendarView
        entryDates={calendarDates}
        onDateSelect={(date) => void handleDateSelect(date)}
      />
    </div>
  );

  return (
    <MainLayout sidebar={sidebarContent}>
      {/* Sync status in header area */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {selectedDate && (
            <h2 className="text-heading-2 text-text-heading font-semibold">
              {format(new Date(selectedDate + 'T00:00:00'), 'EEEE, MMMM d, yyyy')}
            </h2>
          )}
          {isSaving && (
            <span className="text-xs text-text-secondary animate-pulse">Saving...</span>
          )}
          {saveError && retryCount > 0 && (
            <span className="text-xs text-primary-dark">
              Save failed (retry {retryCount}/3)
            </span>
          )}
        </div>
        <SyncStatusIndicator
          entryLoadError={entryLoadError}
          apiTimeout={apiTimeout}
          onRetryEntryLoad={handleRetryEntryLoad}
          className="shrink-0"
        />
      </div>

      {/* Conflict notification banner */}
      {conflicts.length > 0 && (
        <div className="mb-4">
          <ConflictNotification
            conflictCount={conflicts.length}
            onViewConflicts={() => {
              // Open conflict resolution UI - handled by ConflictResolution component
            }}
          />
        </div>
      )}

      {/* Entry Editor */}
      {currentEntry ? (
        <EntryEditor
          content={currentEntry.content}
          onChange={handleEditorChange}
          editable={true}
        />
      ) : (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-16 h-16 mb-4 rounded-full bg-primary-light/50 flex items-center justify-center">
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="text-primary"
              aria-hidden="true"
            >
              <path d="M8 2v4M16 2v4M3 10h18M5 4h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V6a2 2 0 012-2z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className="text-text-secondary text-sm">
            Select a date from the calendar to start writing
          </p>
        </div>
      )}
    </MainLayout>
  );
}

// ─── Root App Component ──────────────────────────────────────────────────────

export function App() {
  const { isAuthenticated, isLoading, initialize, logout, checkInactivity, updateLastActivity } = useAuthStore();
  const [appInitialized, setAppInitialized] = useState(false);

  // Log every render with current auth state
  console.log('[AUTH] App render - isAuthenticated:', isAuthenticated, 'isLoading:', isLoading, 'appInitialized:', appInitialized);

  // ─── Initialize auth on app startup ────────────────────────────────────────

  useEffect(() => {
    async function init() {
      console.log('[AUTH] App useEffect: calling initialize()...');
      await initialize();
      console.log('[AUTH] App useEffect: initialize() complete, setting appInitialized=true');
      setAppInitialized(true);
    }
    void init();
  }, [initialize]);

  // ─── Track user activity for inactivity timeout ────────────────────────────

  useEffect(() => {
    if (!isAuthenticated) return;

    const activityEvents = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'] as const;

    const handleActivity = () => {
      updateLastActivity();
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [isAuthenticated, updateLastActivity]);

  // ─── Periodic inactivity check (every 60 seconds) ─────────────────────────

  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      checkInactivity();
    }, 60_000);

    return () => clearInterval(interval);
  }, [isAuthenticated, checkInactivity]);

  // ─── Loading state during initialization ───────────────────────────────────

  if (!appInitialized || isLoading) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 animate-pulse">
          <div className="w-12 h-12 rounded-full bg-primary-light" />
          <p className="text-sm text-text-secondary">Loading Kira...</p>
        </div>
      </div>
    );
  }

  // ─── Unauthenticated → show login/register screens ─────────────────────────

  if (!isAuthenticated) {
    return <AuthScreens />;
  }

  // ─── Authenticated → show App Shell wrapped in AuthGuard ───────────────────

  return (
    <AuthGuard
      authState={{
        isAuthenticated: true,
        idToken: null, // Token is managed by TokenManager internally
        clearSession: () => void logout(),
      }}
      onUnauthenticated={() => {
        // Already handled by auth store
      }}
    >
      <AppShell />
    </AuthGuard>
  );
}
